import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parrainageAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Trophy, Target, Activity, LayoutDashboard, TrendingUp, Users, FolderKanban, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ParrainCommissionFeed from '@/components/dashboard/ParrainCommissionFeed';
import { useParrainageInsights } from './useParrainageInsights';
import { DossierModuleStatuses, ModuleStatusSummary } from './DossierModuleStatuses';

const PERIODS = [
    { value: 'all', label: 'Tout' },
    { value: 'month', label: 'Ce mois' },
    { value: 'last_month', label: 'Mois dernier' },
    { value: 'year', label: 'Cette année' },
];

const euro = (n) => `${(n || 0).toLocaleString('fr-FR')} €`;

const EVENT_LABEL = {
    new_dossier: 'Nouveau dossier',
    dossier_won: 'Dossier gagné',
    dossier_lost: 'Dossier perdu',
    commission_validated: 'Commission validée',
};

function Stat({ label, value, accent = 'slate', icon: Icon }) {
    return (
        <div className={`rounded-xl border border-${accent}-200 bg-${accent}-50/60 p-4`} data-testid={`stat-${label}`}>
            <div className="flex items-center gap-2 text-xs text-slate-500">{Icon && <Icon className="w-4 h-4" />}{label}</div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        </div>
    );
}

export function ParrainageInsights({ role, effectiveVp, isAdmin, isVP, isParrain }) {
    const navigate = useNavigate();
    const [period, setPeriod] = useState('all');
    const [objForm, setObjForm] = useState({ period: new Date().toISOString().slice(0, 7), target_dossiers: '' });
    const [savingObj, setSavingObj] = useState(false);
    const [sortBy, setSortBy] = useState('gains');
    const { dashboard, leaderboard, activity, objectives, loading, error, load } = useParrainageInsights({ period, isAdmin, isParrain, effectiveVp });

    const handleSetObjective = async () => {
        if (!objForm.target_dossiers || Number(objForm.target_dossiers) < 0) { toast.error('Objectif invalide'); return; }
        setSavingObj(true);
        try {
            await parrainageAPI.setObjective({
                vp_id: isAdmin ? effectiveVp : undefined,
                period: objForm.period,
                target_dossiers: Number(objForm.target_dossiers),
            });
            toast.success('Objectif enregistré');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setSavingObj(false); }
    };

    const sortedLeaderboard = [...leaderboard].sort((a, b) => sortBy === 'gains' ? b.gains - a.gains : b.dossiers - a.dossiers);

    if (isAdmin && !effectiveVp && !isParrain) {
        return <Card><CardContent className="py-10 text-center text-slate-500" data-testid="insights-select-vp">Sélectionnez un VP pour afficher son tableau de bord.</CardContent></Card>;
    }

    return (
        <div className="space-y-4" data-testid="parrainage-insights">
            {/* Filtre période */}
            <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="text-xs text-slate-500">Période</span>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-40 h-9" data-testid="insights-period"><SelectValue /></SelectTrigger>
                    <SelectContent data-testid="insights-period-options">{PERIODS.map(p => <SelectItem key={p.value} value={p.value} data-testid={`insights-period-${p.value}`}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={load} disabled={loading} title="Actualiser le pilotage" aria-label="Actualiser le pilotage" data-testid="insights-refresh">
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>
            {error && <p role="alert" className="text-sm text-red-700" data-testid="insights-refresh-error">{error}</p>}

            <Tabs defaultValue="dashboard">
                <TabsList className="h-auto max-w-full flex flex-wrap justify-start gap-1" data-testid="insights-tabs">
                    <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="w-4 h-4 mr-1.5" />Tableau de bord</TabsTrigger>
                    <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="w-4 h-4 mr-1.5" />Classement</TabsTrigger>
                    {!isParrain && <TabsTrigger value="objectives" data-testid="tab-objectives"><Target className="w-4 h-4 mr-1.5" />Objectifs</TabsTrigger>}
                </TabsList>

                {/* ---------- TABLEAU DE BORD ---------- */}
                <TabsContent value="dashboard" className="space-y-4 mt-4">
                    {!dashboard ? (
                        <Card><CardContent className="py-10 text-center text-slate-500" data-testid="insights-loading-state">{loading ? 'Chargement…' : error ? 'Données indisponibles.' : 'Aucune donnée.'}</CardContent></Card>
                    ) : dashboard.role === 'parrain' ? (
                        <>
                            {/* Fil de commissions temps réel (polling 30 s) — récemment gagnées + à aller chercher */}
                            <ParrainCommissionFeed user={{ role: 'parrain' }} />
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <Stat label="Mes filleuls" value={dashboard.filleuls_total} icon={Users} />
                                <Stat label="Dossiers réseau" value={dashboard.dossiers_count} icon={FolderKanban} />
                                <Stat label="Gagnés" value={dashboard.dossiers_by_status?.won || 0} accent="emerald" icon={CheckCircle2} />
                                <Stat label="Gains ce mois" value={euro(dashboard.gains_this_month)} accent="emerald" icon={TrendingUp} />
                            </div>
                            <GainsBreakdown breakdown={dashboard.gains_breakdown} hasDescendants={dashboard.has_descendants} />
                            {dashboard.has_descendants && (
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                    {[1, 2, 3].map(lvl => (
                                        <div key={lvl} className="rounded-lg bg-white border border-slate-200 p-3 text-center">
                                            <p className="text-xs text-slate-500">Filleuls Niveau {lvl}</p>
                                            <p className="text-xl font-semibold text-slate-900">{dashboard.filleuls_by_level?.[lvl] || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <ModuleStatusSummary counts={dashboard.modules_counts} />
                            <DossiersTable dossiers={dashboard.dossiers} navigate={navigate} allowFilters />
                            <EvolutionCard evolution={dashboard.evolution} />
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <Stat label="Taille du réseau" value={dashboard.network_total} icon={Users} />
                                <Stat label="Dossiers" value={dashboard.dossiers_counts?.total || 0} icon={FolderKanban} />
                                <Stat label="Dossiers gagnés" value={dashboard.dossiers_counts?.won || 0} accent="emerald" icon={CheckCircle2} />
                                <Stat label="Commissions parrains" value={euro(dashboard.total_commissions)} accent="indigo" icon={TrendingUp} />
                            </div>
                            {dashboard.objective && (
                                <Card data-testid="objective-gauge">
                                    <CardContent className="py-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">Objectif dossiers — {dashboard.objective.period}</span>
                                            <span className="text-sm text-slate-500">{dashboard.objective.current} / {dashboard.objective.target_dossiers ?? '—'}</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                                            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Math.min(dashboard.objective.progress || 0, 100)}%` }} />
                                        </div>
                                        {dashboard.objective.target_dossiers == null && <p className="text-xs text-slate-400 mt-2">Aucun objectif défini ce mois-ci.</p>}
                                    </CardContent>
                                </Card>
                            )}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                {[1, 2, 3].map(lvl => (
                                    <div key={lvl} className="rounded-lg bg-white border border-slate-200 p-3 text-center">
                                        <p className="text-xs text-slate-500">Parrains Niveau {lvl}</p>
                                        <p className="text-xl font-semibold text-slate-900">{dashboard.network_by_level?.[lvl] || 0}</p>
                                    </div>
                                ))}
                            </div>
                            {dashboard.top_parrains?.length > 0 && (
                                <Card>
                                    <CardHeader className="pb-2"><CardTitle className="text-base">Top parrains (gains)</CardTitle></CardHeader>
                                    <CardContent className="space-y-1">
                                        {dashboard.top_parrains.map((p, i) => (
                                            <div key={p.parrain_id} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                                                <span className="text-slate-700"><span className="font-semibold text-slate-400 mr-2">#{i + 1}</span>{p.parrain_name} <span className="text-slate-400">· N{p.level}</span></span>
                                                <span className="font-semibold text-indigo-800">{euro(p.gains)}</span>
                                            </div>
                                        ))}
                                    </CardContent>
                                </Card>
                            )}
                            <ModuleStatusSummary counts={dashboard.modules_counts} />
                            <DossiersTable dossiers={dashboard.dossiers} navigate={navigate} />
                            <ActivityCard activity={activity} />
                        </>
                    )}
                </TabsContent>

                {/* ---------- CLASSEMENT ---------- */}
                <TabsContent value="leaderboard" className="mt-4">
                    <Card>
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Classement des parrains</CardTitle>
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-44 h-9" data-testid="leaderboard-sort"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="gains">Trier par gains</SelectItem>
                                    <SelectItem value="dossiers">Trier par dossiers</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardHeader>
                        <CardContent>
                            {sortedLeaderboard.length === 0 ? (
                                <p className="py-8 text-center text-slate-500 text-sm">Aucune donnée sur la période.</p>
                            ) : (
                                <div className="space-y-1">
                                    {sortedLeaderboard.map((p, i) => (
                                        <div key={p.parrain_id} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`leaderboard-row-${p.parrain_id}`}>
                                            <span className="text-slate-700 flex items-center gap-2">
                                                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                                                <span className="font-medium">{p.parrain_name}</span>
                                                <Badge variant="outline" className="text-[10px]">N{p.level}</Badge>
                                            </span>
                                            <span className="flex items-center gap-4">
                                                <span className="text-slate-500">{p.dossiers} dossier{p.dossiers > 1 ? 's' : ''}</span>
                                                <span className="font-semibold text-indigo-800">{euro(p.gains)}</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ---------- OBJECTIFS ---------- */}
                {!isParrain && (
                    <TabsContent value="objectives" className="mt-4 space-y-4">
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-indigo-500" />Fixer un objectif mensuel (nombre de dossiers)</CardTitle></CardHeader>
                            <CardContent>
                                {isAdmin && !effectiveVp ? (
                                    <p className="text-sm text-slate-500">Sélectionnez un VP pour définir son objectif.</p>
                                ) : (
                                    <div className="flex flex-wrap items-end gap-3">
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-500">Mois</label>
                                            <Input type="month" value={objForm.period} onChange={e => setObjForm(p => ({ ...p, period: e.target.value }))} className="w-44" data-testid="objective-period" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-slate-500">Objectif (dossiers)</label>
                                            <Input type="number" min={0} value={objForm.target_dossiers} onChange={e => setObjForm(p => ({ ...p, target_dossiers: e.target.value }))} className="w-36" placeholder="Ex : 5" data-testid="objective-target" />
                                        </div>
                                        <Button onClick={handleSetObjective} disabled={savingObj} data-testid="objective-save">Enregistrer</Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-base">Objectifs définis</CardTitle></CardHeader>
                            <CardContent>
                                {objectives.length === 0 ? (
                                    <p className="py-6 text-center text-slate-500 text-sm">Aucun objectif défini.</p>
                                ) : (
                                    <div className="space-y-1">
                                        {objectives.map(o => (
                                            <div key={o.id} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded-lg px-3 py-2">
                                                <span className="text-slate-700">{o.period}</span>
                                                <span className="font-semibold text-indigo-800">{o.target_dossiers} dossier{o.target_dossiers > 1 ? 's' : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function DossierRow({ d, navigate }) {
    const [open, setOpen] = useState(false);
    const lines = d.commissions_breakdown || [];
    const hasLines = lines.length > 0;
    return (
        <div className="w-full text-sm bg-white border border-slate-200 rounded-lg hover:border-indigo-300 transition-colors" data-testid={`insights-dossier-${d.id}`}>
            <div className="flex flex-wrap items-start justify-between px-3 py-2 gap-2">
                <button type="button"
                    onClick={() => hasLines ? setOpen(o => !o) : navigate(`/prospects/${d.id}`)}
                    className="flex-1 basis-full sm:basis-0 min-w-0 flex items-start gap-2 text-left"
                    aria-expanded={open}
                    data-testid={`dossier-toggle-${d.id}`}>
                    {hasLines && (
                        <span className={`inline-block w-3 h-3 border-b-2 border-r-2 border-slate-500 transition-transform ${open ? 'rotate-45 -translate-y-0.5' : '-rotate-45'}`} />
                    )}
                    <span className="min-w-0 text-slate-700 break-words">
                        <span className="font-medium" data-testid={`dossier-name-${d.id}`}>{d.name}</span>
                        {d.beneficiary_name && <span className="text-slate-400" data-testid={`dossier-beneficiary-${d.id}`}> · {d.beneficiary_name}</span>}
                    </span>
                </button>
                <span className="flex flex-wrap items-center gap-1.5">
                    {d.commission_amount > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 whitespace-nowrap" data-testid={`dossier-commission-${d.id}`}>{euro(d.commission_amount)}</Badge>
                    )}
                    <button type="button" onClick={() => navigate(`/prospects/${d.id}`)}
                        className="text-[11px] text-indigo-600 hover:underline whitespace-nowrap ml-1"
                        data-testid={`dossier-open-${d.id}`}>
                        Ouvrir →
                    </button>
                </span>
            </div>
            <DossierModuleStatuses dossier={d} />
            {open && hasLines && (
                <div className="px-3 pb-2 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-1" data-testid={`dossier-commissions-${d.id}`}>
                    <p className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1">Commissions de mon réseau sur ce dossier</p>
                    {lines.map((c, i) => (
                        <div key={i} className={`flex items-center justify-between text-[12px] px-2 py-1 rounded ${c.is_me ? 'bg-indigo-100/60 border border-indigo-200' : 'bg-white border border-slate-100'}`}>
                            <span className="flex items-center gap-2 min-w-0">
                                <Badge variant="outline" className={`text-[9px] uppercase ${c.is_me ? 'bg-indigo-600 text-white border-indigo-600' : ''}`}>
                                    {c.is_me ? 'Moi' : `N${c.level || '?'}`}
                                </Badge>
                                <span className="truncate text-slate-700">{c.parrain_name}</span>
                                {c.module && (
                                    <Badge variant="outline" className="text-[9px] uppercase">{c.module === 'easy_power' ? 'EP' : 'CH'}</Badge>
                                )}
                            </span>
                            <span className={`font-semibold whitespace-nowrap ${c.is_me ? 'text-indigo-800' : 'text-slate-700'}`}>{euro(c.amount)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DossiersTable({ dossiers, navigate, allowFilters = false }) {
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    if (!dossiers || dossiers.length === 0) {
        return <Card><CardContent className="py-6 text-center text-slate-500 text-sm">Aucun dossier sur la période.</CardContent></Card>;
    }

    const filtered = dossiers.filter(d => {
        if (typeFilter !== 'all') {
            const types = d.dossier_types || [];
            if (!types.includes(typeFilter)) return false;
        }
        if (statusFilter !== 'all') {
            const bs = d.business_status || 'to_advance';
            if (bs !== statusFilter) return false;
        }
        return true;
    });

    return (
        <Card data-testid="insights-dossiers">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-base">Dossiers Parrainage {allowFilters && <span className="text-xs font-normal text-slate-400">({filtered.length}/{dossiers.length})</span>}</CardTitle>
                    {allowFilters && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="w-40 h-8" data-testid="dossier-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les types</SelectItem>
                                    <SelectItem value="chantier">Travaux d’optimisation</SelectItem>
                                    <SelectItem value="easy_power">Courtage</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-44 h-8" data-testid="dossier-status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les statuts</SelectItem>
                                    <SelectItem value="won">Gagnés</SelectItem>
                                    <SelectItem value="to_close">À closer (com en cours)</SelectItem>
                                    <SelectItem value="to_advance">À faire avancer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-1 max-h-96 overflow-auto">
                {filtered.length === 0 ? (
                    <p className="py-4 text-center text-slate-500 text-sm" data-testid="dossier-filter-empty">Aucun dossier ne correspond à ces filtres.</p>
                ) : filtered.map(d => (
                    <DossierRow key={d.id} d={d} navigate={navigate} />
                ))}
            </CardContent>
        </Card>
    );
}

function GainsBreakdown({ breakdown, hasDescendants }) {
    if (!breakdown) return null;
    return (
        <Card data-testid="gains-breakdown" className="border-indigo-100">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-500" />
                    Répartition de mes commissions
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className={`grid gap-3 ${hasDescendants ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'}`}>
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
                        <p className="text-xs text-indigo-700 font-medium uppercase tracking-wide">Mes dossiers directs</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="gains-direct">{euro(breakdown.direct)}</p>
                    </div>
                    {hasDescendants && (
                        <>
                            <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3">
                                <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">Mes sous-parrains</p>
                                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="gains-team">{euro(breakdown.team)}</p>
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                                <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Total réseau</p>
                                <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="gains-total-breakdown">{euro(breakdown.total)}</p>
                            </div>
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function EvolutionCard({ evolution }) {
    if (!evolution || evolution.length === 0) return null;
    const max = Math.max(...evolution.map(e => e.amount), 1);
    return (
        <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" />Évolution de mes gains</CardTitle></CardHeader>
            <CardContent>
                <div className="flex items-end gap-2 h-32">
                    {evolution.map(e => (
                        <div key={e.month} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full bg-indigo-500 rounded-t" style={{ height: `${Math.max((e.amount / max) * 100, 4)}%` }} title={euro(e.amount)} />
                            <span className="text-[10px] text-slate-400">{e.month.slice(5)}</span>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

function ActivityCard({ activity }) {
    if (!activity || activity.length === 0) return null;
    return (
        <Card data-testid="insights-activity">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-slate-500" />Activité récente</CardTitle></CardHeader>
            <CardContent className="space-y-1 max-h-72 overflow-auto">
                {activity.map(e => (
                    <div key={e.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-slate-100 last:border-0">
                        <Badge variant="outline" className="text-[10px] whitespace-nowrap">{EVENT_LABEL[e.type] || e.type}</Badge>
                        <span className="text-slate-700 truncate">{e.prospect_name}</span>
                        <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{(e.created_at || '').slice(0, 10)}</span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
